/**
 * Analytics & Research - the fourth Digital Marketing page.
 *
 * Four sections:
 *   1) Organic Traffic Trend       - line chart from performance_metrics
 *   2) Average Search Position     - bar  chart from performance_metrics
 *   3) Weekly Performance Log      - editable data table of performance_metrics
 *   4) Keyword Research Sources    - editable data table of keyword_research_sources
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  Plus, ExternalLink, Loader2, Search, LineChart as LineIcon,
  ArrowUp, ArrowDown, Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';


// ─── Constants ───────────────────────────────────────────────────────────
const SOURCE_TYPES = ['Free Tool', 'Paid Tool'];

const SOURCE_TYPE_BADGE = {
  'Free Tool': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Paid Tool': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
};

function formatDate(iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}


// ═══════════════════════════════════════════════════════════════════════════
// KPI Strip - latest week's headline metrics with delta vs. prior entry.
// For "Average Position" a decrease is an improvement (lower rank = higher on
// the SERP), so `invert=true` flips the color logic.
// ═══════════════════════════════════════════════════════════════════════════
function computeDelta(latest, previous, key) {
  const a = latest?.[key];
  const b = previous?.[key];
  if (a == null || b == null || Number.isNaN(Number(a)) || Number.isNaN(Number(b))) {
    return { value: null, pct: null };
  }
  const diff = Number(a) - Number(b);
  const pct = Number(b) !== 0 ? (diff / Number(b)) * 100 : null;
  return { value: diff, pct };
}


function DeltaChip({ delta, invert = false, formatFn }) {
  if (delta.value == null) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono tabular-nums text-muted-foreground/60" data-testid="delta-chip-none">
        <Minus className="h-3 w-3" aria-hidden />
        <span>no baseline</span>
      </span>
    );
  }
  // For traffic/CTR/pages: positive diff = improvement (green).
  // For position (invert=true): negative diff = improvement (green).
  const isImprovement = invert ? delta.value < 0 : delta.value > 0;
  const isFlat = delta.value === 0;
  const color = isFlat
    ? 'text-muted-foreground/70'
    : isImprovement ? 'text-emerald-400' : 'text-red-400';
  const ArrowIcon = isFlat ? Minus : (delta.value > 0 ? ArrowUp : ArrowDown);
  const label = formatFn ? formatFn(delta.value) : delta.value.toLocaleString();
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono tabular-nums font-medium ${color}`} data-testid="delta-chip">
      <ArrowIcon className="h-3 w-3" aria-hidden />
      <span>{label}</span>
      {delta.pct != null && (
        <span className="text-muted-foreground/60 ml-0.5">
          ({delta.pct > 0 ? '+' : ''}{delta.pct.toFixed(1)}%)
        </span>
      )}
    </span>
  );
}


function KpiTile({ label, value, delta, deltaInvert, deltaFormat, testId }) {
  return (
    <div className="rounded-md border border-white/10 bg-card p-4 space-y-2" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl font-medium font-mono tabular-nums leading-none text-foreground">{value}</p>
      <DeltaChip delta={delta} invert={deltaInvert} formatFn={deltaFormat} />
    </div>
  );
}


// Get the anchor date for charts / sort. Newer records use `date_end`; legacy
// records fall back to the single-day `date_recorded`.
function metricAnchor(m) {
  return m.date_end || m.date_recorded || '';
}

function metricRangeLabel(m) {
  const start = m.date_start;
  const end = m.date_end;
  if (start && end && start !== end) {
    return `${formatDate(start, { month: 'short', day: 'numeric', year: 'numeric' })} - ${formatDate(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return formatDate(end || start || m.date_recorded);
}


function AnalyticsKpiStrip({ metrics }) {
  const sorted = useMemo(
    () => [...metrics]
      .filter(m => metricAnchor(m))
      .sort((a, b) => metricAnchor(b).localeCompare(metricAnchor(a))),
    [metrics]
  );

  if (sorted.length === 0) {
    return (
      <Card className="border-white/10 bg-card/60 shadow-none" data-testid="analytics-kpi-strip-empty">
        <CardContent className="p-4 text-center">
          <p className="text-xs text-muted-foreground italic">Add a performance metric to see this week&apos;s KPIs.</p>
        </CardContent>
      </Card>
    );
  }

  const latest = sorted[0];
  const previous = sorted[1];
  const deltaTraffic = computeDelta(latest, previous, 'organic_traffic');
  const deltaPosition = computeDelta(latest, previous, 'average_position');
  const deltaCtr = computeDelta(latest, previous, 'click_through_rate');
  const deltaPages = computeDelta(latest, previous, 'pages_indexed');

  return (
    <div className="space-y-2" data-testid="analytics-kpi-strip">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        <span data-testid="analytics-kpi-latest-date">
          Latest snapshot: <span className="text-foreground/80 font-mono tabular-nums normal-case tracking-normal">{metricRangeLabel(latest)}</span>
        </span>
        {previous && (
          <span className="text-muted-foreground/60">
            vs. <span className="font-mono tabular-nums normal-case tracking-normal">{metricRangeLabel(previous)}</span>
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Organic Traffic"
          value={(latest.organic_traffic || 0).toLocaleString()}
          delta={deltaTraffic}
          deltaFormat={(v) => (v > 0 ? `+${v.toLocaleString()}` : v.toLocaleString())}
          testId="kpi-organic-traffic"
        />
        <KpiTile
          label="Average Position"
          value={latest.average_position ? Number(latest.average_position).toFixed(2) : '-'}
          delta={deltaPosition}
          deltaInvert
          deltaFormat={(v) => (v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2))}
          testId="kpi-average-position"
        />
        <KpiTile
          label="Click-Through Rate"
          value={latest.click_through_rate != null ? `${Number(latest.click_through_rate).toFixed(2)}%` : '-'}
          delta={deltaCtr}
          deltaFormat={(v) => (v > 0 ? `+${v.toFixed(2)} pp` : `${v.toFixed(2)} pp`)}
          testId="kpi-ctr"
        />
        <KpiTile
          label="Pages Indexed"
          value={(latest.pages_indexed || 0).toLocaleString()}
          delta={deltaPages}
          deltaFormat={(v) => (v > 0 ? `+${v.toLocaleString()}` : v.toLocaleString())}
          testId="kpi-pages-indexed"
        />
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Measured chart container - avoids Recharts "-1 width" warnings by only
// mounting the chart once the wrapper has positive dimensions.
// ═══════════════════════════════════════════════════════════════════════════
function MeasuredChartContainer({ className, testId, children }) {
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) {
        setSize(prev => (prev.width !== w || prev.height !== h ? { width: w, height: h } : prev));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className={className} data-testid={testId}>
      {size.width > 0 && size.height > 0 ? children(size.width, size.height) : null}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 - Organic Traffic Trend (line chart)
// ═══════════════════════════════════════════════════════════════════════════
function TrafficTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-card/95 backdrop-blur px-3 py-2 shadow-lg text-xs" data-testid="traffic-tooltip">
      <p className="font-mono tabular-nums text-foreground">{metricRangeLabel(d)}</p>
      <p className="text-primary mt-1">
        Organic Traffic: <span className="font-mono tabular-nums">{(d.organic_traffic || 0).toLocaleString()}</span>
      </p>
      <p className="text-muted-foreground">
        Avg Position: <span className="font-mono tabular-nums">{d.average_position ? d.average_position.toFixed(1) : '-'}</span>
      </p>
      {(d.visitors > 0 || d.visits > 0 || d.views > 0) && (
        <div className="mt-1 pt-1 border-t border-white/5 text-[10px] text-muted-foreground/80">
          <div>Visitors: <span className="font-mono tabular-nums">{(d.visitors || 0).toLocaleString()}</span></div>
          <div>Visits: <span className="font-mono tabular-nums">{(d.visits || 0).toLocaleString()}</span></div>
          <div>Views: <span className="font-mono tabular-nums">{(d.views || 0).toLocaleString()}</span></div>
        </div>
      )}
    </div>
  );
}


function OrganicTrafficChart({ metrics }) {
  const data = useMemo(
    () => [...metrics]
      .filter(m => metricAnchor(m))
      .sort((a, b) => metricAnchor(a).localeCompare(metricAnchor(b)))
      .map(m => ({ ...m, __anchor: metricAnchor(m) })),
    [metrics]
  );

  if (data.length === 0) {
    return (
      <Card className="border-white/10 bg-card shadow-none" data-testid="traffic-chart-empty">
        <CardContent className="p-8 text-center">
          <p className="text-sm text-muted-foreground">No performance data yet. Add a record below to see the trend.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-4">
        <MeasuredChartContainer className="h-64 w-full -mx-1" testId="traffic-chart">
          {(w, h) => (
            <LineChart width={w} height={h} data={data} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" vertical={false} />
              <XAxis
                dataKey="__anchor"
                tickFormatter={(v) => formatDate(v, { month: 'short', day: 'numeric' })}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <RechartsTooltip
                cursor={{ stroke: 'hsl(var(--primary))', strokeDasharray: '3 3' }}
                content={<TrafficTooltip />}
              />
              <Line
                type="monotone"
                dataKey="organic_traffic"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 3, fill: 'hsl(var(--primary))' }}
                activeDot={{ r: 5, fill: 'hsl(var(--primary))' }}
              />
            </LineChart>
          )}
        </MeasuredChartContainer>
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 - Average Search Position (bar chart)
// ═══════════════════════════════════════════════════════════════════════════
function PositionTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-card/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      <p className="font-mono tabular-nums text-foreground">{metricRangeLabel(d)}</p>
      <p className="text-primary mt-1">
        Avg Position: <span className="font-mono tabular-nums">{d.average_position ? d.average_position.toFixed(2) : '-'}</span>
      </p>
    </div>
  );
}


function AveragePositionChart({ metrics }) {
  const data = useMemo(
    () => [...metrics]
      .filter(m => metricAnchor(m))
      .sort((a, b) => metricAnchor(a).localeCompare(metricAnchor(b)))
      .map(m => ({ ...m, __anchor: metricAnchor(m) })),
    [metrics]
  );

  if (data.length === 0) {
    return (
      <Card className="border-white/10 bg-card shadow-none" data-testid="position-chart-empty">
        <CardContent className="p-8 text-center">
          <p className="text-sm text-muted-foreground">No performance data yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-4 space-y-2">
        <MeasuredChartContainer className="h-64 w-full -mx-1" testId="position-chart">
          {(w, h) => (
            <BarChart width={w} height={h} data={data} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 2" vertical={false} />
              <XAxis
                dataKey="__anchor"
                tickFormatter={(v) => formatDate(v, { month: 'short', day: 'numeric' })}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={40}
              />
              <RechartsTooltip
                cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
                content={<PositionTooltip />}
              />
              <Bar
                dataKey="average_position"
                fill="hsl(var(--primary))"
                radius={[3, 3, 0, 0]}
              />
            </BarChart>
          )}
        </MeasuredChartContainer>
        <p className="text-[11px] text-muted-foreground italic pl-2" data-testid="position-chart-caption">
          Lower position numbers indicate higher rankings.
        </p>
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 - Weekly Performance Log (data table + create/edit modal)
// ═══════════════════════════════════════════════════════════════════════════
function weekLabel(week) {
  if (!week) return '-';
  const num = week.week_number ? `Week ${week.week_number}` : 'Week ?';
  const dr = week.date_range ? ` - ${formatDate(week.date_range)}` : '';
  return `${num}${dr}`;
}


function PerformanceLog({ metrics, weeksById, strategiesById, canEdit, onRowClick, onAddClick }) {
  const rows = useMemo(
    () => [...metrics].sort((a, b) => (metricAnchor(b) || '').localeCompare(metricAnchor(a) || '')),
    [metrics]
  );

  const formatNkr = (nkr) => {
    if (!nkr) return '';
    if (Array.isArray(nkr)) return nkr.join(', ');
    return String(nkr);
  };

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Weekly Performance Log</h3>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">All performance metrics, most recent first. Click a row to edit.</p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onAddClick} className="gap-1.5" data-testid="add-performance-metric-btn">
              <Plus className="h-3.5 w-3.5" /> Add New
            </Button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No performance metrics yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Date Range</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Strategy</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Visitors</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Visits</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Views</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Organic Traffic</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Avg Position</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">CTR</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">New Keywords Ranking</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(m => {
                const strategy = strategiesById[m.monthly_strategy_id];
                return (
                  <TableRow
                    key={m.id}
                    className="border-white/10 cursor-pointer hover:bg-secondary/30"
                    onClick={() => onRowClick(m)}
                    data-testid={`performance-log-row-${m.id}`}
                  >
                    <TableCell className="text-sm font-mono tabular-nums whitespace-nowrap">{metricRangeLabel(m)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {strategy
                        ? (strategy.month ? formatDate(strategy.month, { month: 'short', year: 'numeric' }) : strategy.theme || '-')
                        : (m.week_id ? weekLabel(weeksById[m.week_id]) : <span className="italic text-muted-foreground/60">-</span>)}
                    </TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{(m.visitors || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{(m.visits || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{(m.views || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{(m.organic_traffic || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{m.average_position ? m.average_position.toFixed(2) : '-'}</TableCell>
                    <TableCell className="text-sm font-mono tabular-nums text-right">{m.click_through_rate ? `${m.click_through_rate.toFixed(2)}%` : '-'}</TableCell>
                    <TableCell className="text-sm text-foreground/90 max-w-[280px] truncate">{formatNkr(m.new_keywords_ranking) || <span className="italic text-muted-foreground/60">-</span>}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Performance Metric Modal (create + edit) with searchable week picker
// ═══════════════════════════════════════════════════════════════════════════
function WeekPicker({ value, onChange, weeks, disabled }) {
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return weeks;
    return weeks.filter(w => {
      const label = `week ${w.week_number || ''} ${w.date_range || ''}`.toLowerCase();
      return label.includes(ql);
    });
  }, [weeks, q]);

  const selected = weeks.find(w => w.id === value);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search weeks..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
          className="pl-9 bg-background border-white/10 h-9 text-sm"
          data-testid="week-picker-search"
        />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border border-white/10 bg-background/40" data-testid="week-picker-list">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 italic p-3">No weeks match.</p>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onChange('')}
              className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 transition-colors ${!value ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-secondary/40'}`}
              data-testid="week-picker-option-none"
            >
              None (skip week link)
            </button>
            {filtered.map(w => (
              <button
                key={w.id}
                type="button"
                disabled={disabled}
                onClick={() => onChange(w.id)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 last:border-b-0 transition-colors ${value === w.id ? 'bg-primary/15 text-primary' : 'text-foreground/90 hover:bg-secondary/40'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`week-picker-option-${w.id}`}
              >
                {weekLabel(w)}
                {w.status && <span className="ml-2 text-muted-foreground/60">- {w.status}</span>}
              </button>
            ))}
          </>
        )}
      </div>
      {selected && (
        <p className="text-[11px] text-muted-foreground pl-1" data-testid="week-picker-selected">
          Selected: <span className="text-foreground/90">{weekLabel(selected)}</span>
        </p>
      )}
    </div>
  );
}


// Multi-select for keywords sourced from a strategy's linked keyword groups.
// Renders selected keywords as pills at the top; a scrollable list below to
// toggle keywords. Falls back to a placeholder message if no strategy is
// picked yet or the strategy has no keyword groups / keywords.
function KeywordMultiSelect({ selected, onChange, availableKeywords, disabled, emptyMessage }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return availableKeywords;
    return availableKeywords.filter(k => (k.keyword || '').toLowerCase().includes(ql));
  }, [availableKeywords, q]);

  const toggle = (kw) => {
    if (disabled) return;
    if (selected.includes(kw)) {
      onChange(selected.filter(x => x !== kw));
    } else {
      onChange([...selected, kw]);
    }
  };

  return (
    <div className="space-y-2" data-testid="new-keyword-ranking-multi">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="new-keyword-ranking-selected">
          {selected.map(kw => (
            <span
              key={kw}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30"
            >
              {kw}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(kw)}
                  className="hover:text-primary/70"
                  aria-label={`Remove ${kw}`}
                  data-testid={`new-keyword-ranking-remove-${kw}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search keywords..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
          className="pl-9 bg-background border-white/10 h-9 text-sm"
          data-testid="new-keyword-ranking-search"
        />
      </div>
      <div className="max-h-40 overflow-y-auto rounded-md border border-white/10 bg-background/40 divide-y divide-white/5">
        {availableKeywords.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 italic p-3" data-testid="new-keyword-ranking-empty">
            {emptyMessage}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground/60 italic p-3">No keywords match.</p>
        ) : (
          filtered.map(k => {
            const isSelected = selected.includes(k.keyword);
            return (
              <label
                key={k.id}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/40 text-sm"
                data-testid={`new-keyword-ranking-option-${k.id}`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(k.keyword)}
                  disabled={disabled}
                  className="accent-primary"
                />
                <span className="text-foreground truncate flex-1">{k.keyword}</span>
                {k.type && (
                  <span className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">{k.type}</span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}


function PerformanceMetricModal({ open, metric, onClose, onSaved, canEdit, weeks, strategies, allKeywords }) {
  const isNew = !metric?.id;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (metric?.id) {
      // Existing record: seed dates from either the new range fields or the
      // legacy `date_recorded` value so old records still edit cleanly.
      const dStart = metric.date_start || metric.date_recorded || '';
      const dEnd = metric.date_end || metric.date_recorded || '';
      // Legacy records store new_keywords_ranking as a comma-separated string.
      // New records store it as a list. Normalise into an array of strings.
      const rawNkr = metric.new_keywords_ranking;
      let nkrArr = [];
      if (Array.isArray(rawNkr)) {
        nkrArr = rawNkr.slice();
      } else if (typeof rawNkr === 'string' && rawNkr.trim()) {
        nkrArr = rawNkr.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      }
      setForm({
        date_start: dStart, date_end: dEnd,
        monthly_strategy_id: metric.monthly_strategy_id || '',
        organic_traffic: metric.organic_traffic ?? '',
        new_keywords_ranking: nkrArr,
        average_position: metric.average_position ?? '',
        click_through_rate: metric.click_through_rate ?? '',
        pages_indexed: metric.pages_indexed ?? '',
        bounce_rate: metric.bounce_rate ?? '',
        time_on_page: metric.time_on_page || '',
        visitors: metric.visitors ?? '',
        visits: metric.visits ?? '',
        views: metric.views ?? '',
        week_id: metric.week_id || '',
      });
    } else {
      // Default new-record range: full current month
      const now = new Date();
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
      const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
      setForm({
        date_start: first, date_end: last,
        monthly_strategy_id: '',
        organic_traffic: '', new_keywords_ranking: [],
        average_position: '', click_through_rate: '', pages_indexed: '',
        bounce_rate: '', time_on_page: '',
        visitors: '', visits: '', views: '',
        week_id: '',
      });
    }
  }, [open, metric]);

  // Keywords available for the New Keywords Ranking multi-select come from the
  // selected strategy's linked keyword groups (many-to-many) PLUS the groups
  // that already have this strategy as their parent (weekly_keywords.monthly_strategy_id).
  const availableKeywords = useMemo(() => {
    if (!form?.monthly_strategy_id) return [];
    const strategy = strategies.find(s => s.id === form.monthly_strategy_id);
    const linkedIds = new Set(strategy?.linked_keyword_group_ids || []);
    // Also include weeks whose parent IS this strategy
    for (const w of weeks) {
      if (w.monthly_strategy_id === form.monthly_strategy_id) {
        linkedIds.add(w.id);
      }
    }
    if (linkedIds.size === 0) return [];
    return allKeywords.filter(k => linkedIds.has(k.week_id));
  }, [form?.monthly_strategy_id, strategies, weeks, allKeywords]);

  const keywordEmptyMessage = !form?.monthly_strategy_id
    ? 'Pick a Monthly Strategy above to load its keyword groups.'
    : 'This strategy has no linked keyword groups (or none of them contain keywords yet).';

  if (!form) return null;

  const submit = async () => {
    if (!form.date_start || !form.date_end) return toast.error('Date range start and end are required');
    if (form.date_end < form.date_start) return toast.error('End date must be after start date');
    setSaving(true);
    try {
      const payload = {
        date_start: form.date_start,
        date_end: form.date_end,
        // date_recorded kept in sync server-side; also send it so any older client
        // reading the value still sees the anchor date.
        date_recorded: form.date_end,
        monthly_strategy_id: form.monthly_strategy_id || '',
        organic_traffic: Number(form.organic_traffic) || 0,
        new_keywords_ranking: form.new_keywords_ranking,
        average_position: Number(form.average_position) || 0,
        click_through_rate: Number(form.click_through_rate) || 0,
        pages_indexed: Number(form.pages_indexed) || 0,
        bounce_rate: Number(form.bounce_rate) || 0,
        time_on_page: form.time_on_page,
        visitors: Number(form.visitors) || 0,
        visits: Number(form.visits) || 0,
        views: Number(form.views) || 0,
        week_id: form.week_id || '',
      };
      const res = isNew
        ? await api.post('/performance-metrics', payload)
        : await api.put(`/performance-metrics/${metric.id}`, payload);
      toast.success(isNew ? 'Metric recorded' : 'Metric saved');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!metric?.id) return;
    if (!window.confirm('Delete this performance metric?')) return;
    setDeleting(true);
    try {
      await api.delete(`/performance-metrics/${metric.id}`);
      toast.success('Metric deleted');
      onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="performance-metric-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Performance Metric' : 'Edit Performance Metric'}</DialogTitle>
          <DialogDescription>Track SEO progress across a date range. Link a Monthly Strategy to pull its keywords into the ranking picker.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Date Range Start" testId="pm-date-start">
            <Input type="date" value={form.date_start} disabled={!canEdit} onChange={(e) => setForm({ ...form, date_start: e.target.value })} className="bg-background border-white/10" data-testid="pm-date-start-input" />
          </FormField>
          <FormField label="Date Range End" testId="pm-date-end">
            <Input type="date" value={form.date_end} disabled={!canEdit} onChange={(e) => setForm({ ...form, date_end: e.target.value })} className="bg-background border-white/10" data-testid="pm-date-end-input" />
          </FormField>
          <FormField label="Monthly Strategy" testId="pm-strategy" className="md:col-span-2">
            <Select
              value={form.monthly_strategy_id || 'none'}
              onValueChange={(v) => setForm({ ...form, monthly_strategy_id: v === 'none' ? '' : v, new_keywords_ranking: v === 'none' ? form.new_keywords_ranking : [] })}
              disabled={!canEdit}
            >
              <SelectTrigger className="bg-background border-white/10" data-testid="pm-strategy-trigger"><SelectValue placeholder="Pick a strategy to link" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">- None -</SelectItem>
                {strategies.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.month ? formatDate(s.month, { month: 'short', year: 'numeric' }) : '(no month)'} - {s.theme || 'No theme'} ({s.status || 'Draft'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {/* Umami analytics */}
          <FormField label="Visitors (unique)" testId="pm-visitors">
            <Input type="number" min="0" value={form.visitors} disabled={!canEdit} onChange={(e) => setForm({ ...form, visitors: e.target.value })} className="bg-background border-white/10" data-testid="pm-visitors-input" placeholder="Umami: unique visitors" />
          </FormField>
          <FormField label="Visits" testId="pm-visits">
            <Input type="number" min="0" value={form.visits} disabled={!canEdit} onChange={(e) => setForm({ ...form, visits: e.target.value })} className="bg-background border-white/10" data-testid="pm-visits-input" placeholder="Umami: sessions" />
          </FormField>
          <FormField label="Views" testId="pm-views">
            <Input type="number" min="0" value={form.views} disabled={!canEdit} onChange={(e) => setForm({ ...form, views: e.target.value })} className="bg-background border-white/10" data-testid="pm-views-input" placeholder="Umami: pageviews" />
          </FormField>
          <FormField label="Time on Page" testId="pm-time">
            <Input value={form.time_on_page} disabled={!canEdit} onChange={(e) => setForm({ ...form, time_on_page: e.target.value })} className="bg-background border-white/10" data-testid="pm-time-input" placeholder="e.g. 2:15" />
          </FormField>

          {/* Existing SEO metrics */}
          <FormField label="Organic Traffic" testId="pm-traffic">
            <Input type="number" min="0" value={form.organic_traffic} disabled={!canEdit} onChange={(e) => setForm({ ...form, organic_traffic: e.target.value })} className="bg-background border-white/10" data-testid="pm-traffic-input" />
          </FormField>
          <FormField label="Average Position" testId="pm-position">
            <Input type="number" step="0.01" min="0" value={form.average_position} disabled={!canEdit} onChange={(e) => setForm({ ...form, average_position: e.target.value })} className="bg-background border-white/10" data-testid="pm-position-input" />
          </FormField>
          <FormField label="Click-Through Rate (%)" testId="pm-ctr">
            <Input type="number" step="0.01" min="0" value={form.click_through_rate} disabled={!canEdit} onChange={(e) => setForm({ ...form, click_through_rate: e.target.value })} className="bg-background border-white/10" data-testid="pm-ctr-input" />
          </FormField>
          <FormField label="Bounce Rate (%)" testId="pm-bounce">
            <Input type="number" step="0.01" min="0" value={form.bounce_rate} disabled={!canEdit} onChange={(e) => setForm({ ...form, bounce_rate: e.target.value })} className="bg-background border-white/10" data-testid="pm-bounce-input" />
          </FormField>
          <FormField label="Pages Indexed" testId="pm-pages" className="md:col-span-2">
            <Input type="number" min="0" value={form.pages_indexed} disabled={!canEdit} onChange={(e) => setForm({ ...form, pages_indexed: e.target.value })} className="bg-background border-white/10" data-testid="pm-pages-input" />
          </FormField>

          <FormField label="New Keywords Ranking" testId="pm-newkw" className="md:col-span-2">
            <KeywordMultiSelect
              selected={form.new_keywords_ranking}
              onChange={(v) => setForm({ ...form, new_keywords_ranking: v })}
              availableKeywords={availableKeywords}
              disabled={!canEdit}
              emptyMessage={keywordEmptyMessage}
            />
          </FormField>

          <FormField label="Week (optional)" testId="pm-week" className="md:col-span-2">
            <WeekPicker
              value={form.week_id}
              onChange={(v) => setForm({ ...form, week_id: v })}
              weeks={weeks}
              disabled={!canEdit}
            />
          </FormField>
        </div>
        <DialogFooter className="sm:justify-between">
          <div>
            {!isNew && canEdit && (
              <Button variant="ghost" onClick={remove} disabled={saving || deleting} className="text-destructive hover:text-destructive" data-testid="pm-delete-btn">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Close</Button>
            {canEdit && (
              <Button onClick={submit} disabled={saving || deleting} className="bg-primary text-primary-foreground gap-1.5" data-testid="pm-save-btn">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 - Keyword Research Sources (data table + create/edit modal)
// ═══════════════════════════════════════════════════════════════════════════
function KeywordSourcesTable({ sources, canEdit, onRowClick, onAddClick }) {
  const rows = useMemo(
    () => [...sources].sort((a, b) => (b.last_used || '').localeCompare(a.last_used || '')),
    [sources]
  );

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Keyword Research Sources</h3>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">Free and paid tools the SEO team uses. Click a row to edit.</p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onAddClick} className="gap-1.5" data-testid="add-keyword-source-btn">
              <Plus className="h-3.5 w-3.5" /> Add New
            </Button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No research sources yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Source Name</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Type</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">URL</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Rating</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Last Used</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(s => (
                <TableRow
                  key={s.id}
                  className="border-white/10 cursor-pointer hover:bg-secondary/30"
                  onClick={() => onRowClick(s)}
                  data-testid={`keyword-source-row-${s.id}`}
                >
                  <TableCell className="text-sm font-medium">{s.source_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] uppercase ${SOURCE_TYPE_BADGE[s.type] || 'bg-secondary text-muted-foreground border-border'}`}>
                      {s.type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {s.url ? (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[280px]"
                        data-testid={`keyword-source-url-${s.id}`}
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{s.url}</span>
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground/60 italic">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right" data-testid={`keyword-source-rating-${s.id}`}>
                    <span className="text-foreground">{s.rating ?? 0}</span>
                    <span className="text-muted-foreground/60"> / 5</span>
                  </TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-muted-foreground">{formatDate(s.last_used)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{s.notes || <span className="italic text-muted-foreground/60">-</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


function KeywordSourceModal({ open, source, onClose, onSaved, canEdit }) {
  const isNew = !source?.id;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (source?.id) {
      setForm({
        source_name: source.source_name || '',
        type: source.type || 'Free Tool',
        url: source.url || '',
        rating: source.rating ?? 0,
        last_used: source.last_used || '',
        notes: source.notes || '',
      });
    } else {
      setForm({
        source_name: '', type: 'Free Tool', url: '',
        rating: 0, last_used: '', notes: '',
      });
    }
  }, [open, source]);

  if (!form) return null;

  const submit = async () => {
    if (!form.source_name.trim()) return toast.error('Source Name is required');
    const rating = Number(form.rating);
    if (Number.isNaN(rating) || rating < 0 || rating > 5) {
      return toast.error('Rating must be between 0 and 5');
    }
    setSaving(true);
    try {
      const payload = {
        source_name: form.source_name,
        type: form.type,
        url: form.url,
        rating,
        last_used: form.last_used,
        notes: form.notes,
      };
      const res = isNew
        ? await api.post('/keyword-research-sources', payload)
        : await api.put(`/keyword-research-sources/${source.id}`, payload);
      toast.success(isNew ? 'Source added' : 'Source saved');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!source?.id) return;
    if (!window.confirm('Delete this research source? Keywords using it will block the delete.')) return;
    setDeleting(true);
    try {
      await api.delete(`/keyword-research-sources/${source.id}`);
      toast.success('Source deleted');
      onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="keyword-source-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Research Source' : 'Edit Research Source'}</DialogTitle>
          <DialogDescription>Free tools like Google Trends and paid tools like Ahrefs. Rate them 0-5 based on usefulness.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Source Name" testId="ks-name" className="md:col-span-2">
            <Input value={form.source_name} disabled={!canEdit} onChange={(e) => setForm({ ...form, source_name: e.target.value })} className="bg-background border-white/10" data-testid="ks-name-input" placeholder="e.g. Ahrefs" />
          </FormField>
          <FormField label="Type" testId="ks-type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="ks-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Rating (0-5)" testId="ks-rating">
            <Input type="number" min="0" max="5" step="1" value={form.rating} disabled={!canEdit} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="bg-background border-white/10" data-testid="ks-rating-input" />
          </FormField>
          <FormField label="URL" testId="ks-url" className="md:col-span-2">
            <Input value={form.url} disabled={!canEdit} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-background border-white/10" data-testid="ks-url-input" placeholder="https://..." />
          </FormField>
          <FormField label="Last Used" testId="ks-last-used">
            <Input type="date" value={form.last_used} disabled={!canEdit} onChange={(e) => setForm({ ...form, last_used: e.target.value })} className="bg-background border-white/10" data-testid="ks-last-used-input" />
          </FormField>
          <FormField label="Notes" testId="ks-notes" className="md:col-span-2">
            <Textarea rows={3} value={form.notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10" data-testid="ks-notes-input" />
          </FormField>
        </div>
        <DialogFooter className="sm:justify-between">
          <div>
            {!isNew && canEdit && (
              <Button variant="ghost" onClick={remove} disabled={saving || deleting} className="text-destructive hover:text-destructive" data-testid="ks-delete-btn">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Close</Button>
            {canEdit && (
              <Button onClick={submit} disabled={saving || deleting} className="bg-primary text-primary-foreground gap-1.5" data-testid="ks-save-btn">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Utility form components
// ═══════════════════════════════════════════════════════════════════════════
function FormField({ label, testId, className = '', children }) {
  return (
    <div className={`space-y-1 ${className}`} data-testid={`field-${testId}`}>
      <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}


function SectionHeader({ label }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="h-px flex-1 bg-white/5" aria-hidden />
      <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">{label}</p>
      <div className="h-px flex-1 bg-white/5" aria-hidden />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// PAGE COMPOSITION
// ═══════════════════════════════════════════════════════════════════════════
export default function AnalyticsResearchPage() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm('manage_digital_marketing');

  const [metrics, setMetrics] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [sources, setSources] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [allKeywords, setAllKeywords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [metricModal, setMetricModal] = useState({ open: false, metric: null });
  const [sourceModal, setSourceModal] = useState({ open: false, source: null });

  const reloadAll = async () => {
    try {
      const [m, w, s, st, k] = await Promise.all([
        api.get('/performance-metrics'),
        api.get('/weekly-keywords'),
        api.get('/keyword-research-sources'),
        api.get('/monthly-strategies'),
        api.get('/keywords'),
      ]);
      setMetrics(m.data || []);
      setWeeks(w.data || []);
      setSources(s.data || []);
      setStrategies(st.data || []);
      setAllKeywords(k.data || []);
    } catch (err) {
      console.error('Analytics fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadAll(); }, []);

  const weeksById = useMemo(() => Object.fromEntries(weeks.map(w => [w.id, w])), [weeks]);
  const strategiesById = useMemo(() => Object.fromEntries(strategies.map(s => [s.id, s])), [strategies]);

  return (
    <div className="p-6 space-y-6" data-testid="analytics-research-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-primary/10 border border-primary/20">
          <LineIcon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-[22px] font-medium tracking-tight" data-testid="analytics-title">Analytics &amp; Research</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Weekly SEO performance, trend charts, and the research tools that power keyword discovery.</p>
        </div>
      </div>

      {/* Section 1 - Organic Traffic Trend */}
      <SectionHeader label="Organic Traffic Trend" />
      {loading ? <SkeletonBlock /> : (
        <div className="space-y-4">
          <AnalyticsKpiStrip metrics={metrics} />
          <OrganicTrafficChart metrics={metrics} />
        </div>
      )}

      {/* Section 2 - Average Search Position */}
      <SectionHeader label="Average Search Position Over Time" />
      {loading ? <SkeletonBlock /> : <AveragePositionChart metrics={metrics} />}

      {/* Section 3 - Weekly Performance Log */}
      <SectionHeader label="Weekly Performance Log" />
      <PerformanceLog
        metrics={metrics}
        weeksById={weeksById}
        strategiesById={strategiesById}
        canEdit={canEdit}
        onRowClick={(m) => setMetricModal({ open: true, metric: m })}
        onAddClick={() => setMetricModal({ open: true, metric: null })}
      />

      {/* Section 4 - Keyword Research Sources */}
      <SectionHeader label="Keyword Research Sources" />
      <KeywordSourcesTable
        sources={sources}
        canEdit={canEdit}
        onRowClick={(s) => setSourceModal({ open: true, source: s })}
        onAddClick={() => setSourceModal({ open: true, source: null })}
      />

      {/* Modals */}
      <PerformanceMetricModal
        open={metricModal.open}
        metric={metricModal.metric}
        canEdit={canEdit}
        weeks={weeks}
        strategies={strategies}
        allKeywords={allKeywords}
        onClose={() => setMetricModal({ open: false, metric: null })}
        onSaved={() => reloadAll()}
      />
      <KeywordSourceModal
        open={sourceModal.open}
        source={sourceModal.source}
        canEdit={canEdit}
        onClose={() => setSourceModal({ open: false, source: null })}
        onSaved={() => reloadAll()}
      />
    </div>
  );
}


function SkeletonBlock() {
  return (
    <div className="rounded-md border border-white/10 bg-card/40 p-4">
      <div className="h-4 bg-white/5 rounded w-1/4 animate-pulse mb-3" />
      <div className="h-3 bg-white/5 rounded w-2/3 animate-pulse mb-2" />
      <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
    </div>
  );
}
